import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { getStockTable, errorMessage } from '../services/api';
import { LoadError } from '../components/LoadError';
import { ClipboardList } from 'lucide-react';

export default function StockTable() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoadError(null);
            const res = await getStockTable();
            setData(res.data);
        } catch (error) {
            if (!error._isAuthError) setLoadError(errorMessage(error, "Erreur lors du chargement de l'état du stock"));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="p-4 sm:p-6 space-y-4">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-96" />
                </div>
            </Layout>
        );
    }

    if (loadError) {
        return (
            <Layout>
                <div className="p-4 sm:p-6">
                    <LoadError message={loadError} onRetry={() => { setLoading(true); fetchData(); }} />
                </div>
            </Layout>
        );
    }

    const rows = data?.rows || [];
    const totals = data?.totals || { en_stock: 0, hors_stock: 0, chez_client: 0 };
    const totalGlobal = totals.en_stock + totals.hors_stock + totals.chez_client;

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-6 animate-fade-in" data-testid="stock-table-page">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight">ÉTAT DU STOCK</h1>
                    <p className="text-muted-foreground">Répartition des disques par projet</p>
                </div>

                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                            <ClipboardList className="w-5 h-5" />
                            {rows.length} projet{rows.length > 1 ? 's' : ''}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-sm border border-border overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Projet</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider text-center w-[140px]">En stock</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider text-center w-[140px]">Hors stock</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider text-center w-[140px]">Chez le client</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider text-center w-[100px]">Total</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {/* Total row at top */}
                                    <TableRow className="bg-secondary/60 hover:bg-secondary/60 border-b-2 border-border" data-testid="stock-totals-row">
                                        <TableCell className="font-bold text-xs uppercase tracking-wider">Total</TableCell>
                                        <TableCell className="text-center">
                                            <span className="font-bold text-tone-stock">{totals.en_stock}</span>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <span className="font-bold text-tone-neutre">{totals.hors_stock}</span>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <span className="font-bold text-tone-client">{totals.chez_client}</span>
                                        </TableCell>
                                        <TableCell className="text-center font-bold tabular-nums">{totalGlobal}</TableCell>
                                    </TableRow>
                                    {rows.map((row, i) => {
                                        const rowTotal = row.en_stock + row.hors_stock + row.chez_client;
                                        return (
                                            <TableRow key={i} className="hover:bg-muted/30" data-testid={`stock-row-${i}`}>
                                                <TableCell className="font-medium">{row.project_title}</TableCell>
                                                <TableCell className="text-center">
                                                    {row.en_stock > 0 ? (
                                                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-semibold bg-tone-stock-base/15 text-tone-stock">{row.en_stock}</span>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    {row.hors_stock > 0 ? (
                                                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-semibold bg-tone-neutre-base/15 text-tone-neutre">{row.hors_stock}</span>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    {row.chez_client > 0 ? (
                                                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-semibold bg-tone-client-base/15 text-tone-client">{row.chez_client}</span>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-center font-semibold">{rowTotal}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
